import lodash from 'lodash';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import encryptPassword from '@/mixins/encryptPassword';
import languagePicker from '@/mixins/languagePicker';
import preventionData from '@/mixins/preventionData';
import checkUserVerification from '@/mixins/checkUserVerification';
import webChats from '@/mixins/webChats';
import { multifactor as multifactorModule } from '@/store/forms/multifactor';

export default {
  mixins: [encryptPassword, languagePicker, preventionData, checkUserVerification, webChats],
  created() {
    if (!this.$store.hasModule('forms')) {
      this.$store.registerModule('forms', { namespaced: true });
    }
    if (!this.$store.hasModule(['forms', 'multifactor'])) {
      this.$store.registerModule(['forms', 'multifactor'], multifactorModule);
    }
    const twoFactorAuthorizationTypeId = this.$auth?.user?.twoFactorAuthorizationTypeId ?? null;
    this.$store.dispatch('forms/multifactor/hydrateFromAuth', { twoFactorAuthorizationTypeId });
    if (!this.$auth?.user?.phone) {
      const current = this.$store.state.forms.multifactor.twoFactorAuthorizationTypeId;
      if (current === 1) {
        this.$store.commit('forms/multifactor/setTwoFactorAuthorizationTypeId', null);
      }
    }
  },
  methods: {
    validateField(eventName) {
      return new Promise(resolve => {
        this.$eventHub.$emit(eventName, () => {
          resolve();
        });
      });
    },

    async submitBasicData(checkSecurity) {
      await this.validateField('validateFirstName');
      await this.validateField('validateLastName');
      await this.validateField('validatePhone');
      await this.validateField('validateDateSegmented');
      await this.validateField('validateSex');

      if (this.$auth.user.regionId !== 3) {
        await this.validateField('validateInsuranceCompany');
      }

      await this.finishValidation(checkSecurity);
    },

    async finishValidation(checkSecurity) {
      if (checkSecurity && this.$auth.user.securitySection.show) {
        await this.validateField('validateUpdatePassword');
      }
      await this.sentFormBasicData(checkSecurity);
    },

    async sentFormBasicData(checkSecurity) {
      if (
        this.isValidFirstName &&
        this.isValidLastName &&
        this.isValidPhone &&
        this.isValidDate &&
        this.isValidSex &&
        this.validInsuranceCompany &&
        (!checkSecurity ||
          !this.$auth.user.securitySection.show ||
          (this.$auth.user.securitySection.show && this.isValidUpdatePassword))
      ) {
        await this.updateProfile();
      } else {
        this.$nextTick(() => this.scrollToError());
      }
    },

    async updatePassword(setLoading) {
      try {
        await this.$http.put('/v3/fe/web-users/change-password', {
          encryptedOriginalPassword: this.encryptPassword(this.currentPassword),
          encryptedNewPassword: this.encryptPassword(this.newPassword)
        });
        this.$eventHub.$emit('resetUpdatePassword');
        if (setLoading) {
          this.loading = false;
        }
        // The password change invalidates all sessions on the backend
        // (DEVUL-7823), including this one, so force a full logout and re-login.
        await this.$store.dispatch('auth/logout');
        return true;
      } catch (err) {
        this.loading = false;
        const errorType = err.data?.error;
        if (errorType === 'The original password is wrong') {
          this.setCurrentPasswordValidation(false);
          this.setCurrentPasswordError(this.$t('profile.invalidCurrentPassword'));
        } else if (errorType === 'The password was used in history') {
          this.setNewPasswordError(true);
        } else {
          this.handleError({ error: err, code: '0x00000001' });
        }
        return false;
      }
    },

    awaitPhoneVerification() {
      return new Promise(resolve => {
        const cleanup = () => {
          this.$eventHub.$off('phoneVerified', onVerified);
          unwatch();
        };
        const onVerified = () => {
          cleanup();
          resolve(true);
        };
        const unwatch = this.$watch(
          () => this.$store.state.profile.dashboard.dialog,
          dialog => {
            if (dialog !== 'phone-verification') {
              cleanup();
              resolve(false);
            }
          }
        );
        this.$eventHub.$on('phoneVerified', onVerified);
      });
    },

    async setAccountSettingsData() {
      this.language = this.$auth.user && this.$auth.user.language ? this.$auth.user.language : 'cs';
      this.$store.commit('forms/firstName/updateFirstName', this.$auth.user.firstName || '');
      this.$store.commit('forms/lastName/updateLastName', this.$auth.user.lastName || '');
      if (this.$auth.user.phone) {
        const formatted = this.phoneFormatted(this.$auth.user.phone);
        if (formatted) {
          this.$store.commit('forms/phone/updatePhoneNumber', formatted.number.toString());
          this.$store.commit('forms/phone/updatePhoneCountry', formatted.prefix.toString());
        }
      } else {
        this.$store.commit('forms/phone/updatePhoneCountry', this.defaultPhoneCountry);
      }

      if (this.$auth.user.birthDate) {
        const birthDate = new Date(this.$auth.user.birthDate);
        this.$store.commit(
          'forms/date/updateDate',
          `${birthDate.getFullYear()}-${birthDate.getMonth() + 1}-${birthDate.getDate()}`
        );
      }
      this.$store.commit('forms/sex/updateSex', this.$auth.user.gender || null);
      this.$store.commit('forms/addressGoogle/updateAddressText', this.$auth.user.address);
      this.$store.commit('forms/addressGoogle/updateAddressGoogle', {});
      const mfaApiValue = this.$auth?.user?.twoFactorAuthorizationTypeId ?? null;
      this.$store.dispatch('forms/multifactor/hydrateFromAuth', {
        twoFactorAuthorizationTypeId: mfaApiValue
      });
      if (!this.$auth?.user?.phone) {
        const currentMfa = this.$store.state.forms.multifactor.twoFactorAuthorizationTypeId;
        if (currentMfa === 1) {
          this.$store.commit('forms/multifactor/setTwoFactorAuthorizationTypeId', null);
        }
      }
    },

    async updateAccountData(updatedFields) {
      let newLanguage = false;
      if (this.language !== this.$i18n.locale) {
        newLanguage = true;
      }
      this.setTab(updatedFields.sex === 2 ? 1 : 0);

      try {
        // TODO: migrate to patchUser helper
        await this.$http.patch('/v3/fe/web-users', updatedFields);
        for (const [key] of Object.entries(updatedFields)) {
          this.gtmPush({ event: 'profile_update_success', 'profile.field': key });
        }
        await this.$auth.fetchUser();
        if (this.$auth.user.gender === 2) {
          this.setTab(1);
        }

        this.getAllDefaultFormData();
        await this.getPreventionData();
        this.success = true;

        if (this.isFlutterReady) {
          window.flutter_inappwebview.callHandler('onProfileUpdated');
        }

        if (newLanguage) {
          location.reload();
          return;
        }

        if (this.$auth.user.phone && !this.$auth.user.verify.phone) {
          this.setDialog({
            name: 'phone-verification',
            params: { pv_step: 1 }
          });
        }
      } catch (error) {
        this.$store.dispatch('errorModal/handleError', {
          error,
          showModal: false,
          code: '0x00000002'
        });
      } finally {
        this.loading = false;
      }
    },

    ...mapActions({
      getAllDefaultFormData: 'dynamicForms/getAllDefaultFormData',
      setCurrentPasswordValidation: 'forms/updatePassword/setCurrentPasswordValidation',
      setCurrentPasswordError: 'forms/updatePassword/setCurrentPasswordError',
      setNewPasswordError: 'forms/updatePassword/setNewPasswordError'
    }),
    ...mapMutations({
      setTab: 'profile/healthInsuranceProviderBenefit/setTab',
      setDialog: 'profile/dashboard/setDialog'
    })
  },
  computed: {
    defaultPhoneCountry() {
      const defaultRegionId = 1; // Česko default

      const phoneCountriesByRegion = Object.freeze({
        1: { code: 'CZ', prefix: '+420', example: '123 456 789' }, // Česko
        2: { code: 'SK', prefix: '+421', example: '123 456 789' }, // Slovensko
        3: { code: 'HU', prefix: '+36', example: '201234567' } // Maďarsko
      });

      const regionId = this.$auth.user?.regionId ?? defaultRegionId;

      return phoneCountriesByRegion[regionId] ?? phoneCountriesByRegion[defaultRegionId];
    },
    getBasicDataUpdateFields() {
      const updatedFields = {};

      if (this.firstName !== this.$auth.user.firstName) {
        updatedFields.firstName = this.firstName;
      }
      if (this.lastName !== this.$auth.user.lastName) {
        updatedFields.lastName = this.lastName;
      }
      if (
        (!!this.phone && `${this.phone.prefix}${this.phone.number}` !== this.$auth.user.phone) ||
        !this.phone
      ) {
        updatedFields.phone = this.phone;
      }
      if (this.date) {
        let birthDateUpdated = !this.$auth.user.birthDate;
        if (!birthDateUpdated) {
          const localDate = new Date(this.date);
          const webUserDate = new Date(this.$auth.user.birthDate);
          birthDateUpdated =
            localDate.getFullYear() !== webUserDate.getFullYear() ||
            localDate.getMonth() !== webUserDate.getMonth() ||
            localDate.getDate() !== webUserDate.getDate();
        }
        if (birthDateUpdated) {
          updatedFields.birthDate = this.date;
        }
      }
      if (!!this.sex && this.sex !== this.$auth.user.gender) {
        updatedFields.sex = this.sex;
      }
      if (!!this.addressText && this.addressText !== this.$auth.user.address) {
        if (this.addressGoogle && !lodash.isEmpty(this.addressGoogle)) {
          updatedFields.address = this.addressGoogle;
        }
        updatedFields.addressText = this.addressText;
      }
      if (this.insuranceCompany !== this.$auth.user.healthInsuranceCompany) {
        updatedFields.healthInsuranceProviderId = this.insuranceCompany;
      }

      if (this.language !== this.$auth.user.language) {
        updatedFields.language = this.language;
        this.gtmPush({
          event: 'select_language',
          'form-name': 'profile',
          siteSection: 'klientska_zona'
        });
      }
      const currentMfaApiValueRaw =
        this.$store.getters['forms/multifactor/apiTwoFactorAuthorizationTypeId'];
      const currentMfaApiValue = currentMfaApiValueRaw === 0 ? null : currentMfaApiValueRaw;
      const userMfaApiValue = this.$auth?.user?.twoFactorAuthorizationTypeId ?? null;
      if (currentMfaApiValue !== userMfaApiValue) {
        updatedFields.twoFactorAuthorizationTypeId = currentMfaApiValue;
      }
      return updatedFields;
    },
    loading: {
      get() {
        return this.$store.state.profile.accountSettings.loading;
      },
      set(value) {
        this.$store.dispatch('profile/accountSettings/setLoading', value);
      }
    },
    success: {
      get() {
        return this.$store.state.profile.accountSettings.successState;
      },
      set(value) {
        this.$store.dispatch('profile/accountSettings/setSuccess', value);
      }
    },
    showLanguageChangeNotification: {
      get() {
        return this.$store.state.profile.accountSettings.showLanguageChangeNotification;
      },
      set(value) {
        this.$store.commit('profile/accountSettings/setShowLanguageChangeNotification', value);
      }
    },
    validInsuranceCompany() {
      return this.isHu ? true : this.isValidInsuranceCompany;
    },
    ...mapGetters({
      firstName: 'forms/firstName/firstName',
      lastName: 'forms/lastName/lastName',
      phone: 'forms/phone/phone',
      date: 'forms/date/date',
      sex: 'forms/sex/sex',
      insuranceCompany: 'forms/insuranceCompany/insuranceCompany',
      addressGoogle: 'forms/addressGoogle/addressGoogle',
      addressText: 'forms/addressGoogle/addressText',
      currentPassword: 'forms/updatePassword/currentPassword',
      newPassword: 'forms/updatePassword/newPassword',

      isValidFirstName: 'forms/firstName/isValid',
      isValidLastName: 'forms/lastName/isValid',
      isValidPhone: 'forms/phone/isValid',
      isValidDate: 'forms/date/isValid',
      isValidSex: 'forms/sex/isValid',
      isValidInsuranceCompany: 'forms/insuranceCompany/isValid',
      isValidUpdatePassword: 'forms/updatePassword/isValid'
    }),
    resetTimeout: {
      get() {
        return this.$store.state.sendButton.resetTimeout;
      },
      set(value) {
        this.$store.dispatch('sendButton/setResetTimeout', value);
      }
    }
  },
  watch: {
    language(newValue) {
      const foundAppLanguage = this.languageList.find(val => val.id === newValue);
      if (foundAppLanguage) {
        this.language = foundAppLanguage.id;
      } else if (this.languageList.length) {
        this.language = this.languageList[0].id;
      }
    },
    firstName() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    lastName() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    phone() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    date() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    sex() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    insuranceCompany() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    addressGoogle() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    addressText() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    currentPassword() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    newPassword() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    '$store.state.forms.multifactor.twoFactorAuthorizationTypeId'() {
      this.success = false;
      clearTimeout(this.resetTimeout);
    },
    '$store.state.auth.user.address'(newAddress) {
      this.$store.commit('forms/addressGoogle/updateAddressText', newAddress);
      this.$store.commit('forms/addressGoogle/updateAddressGoogle', {});
    }
  }
};
